<template>
  <div class="container">
    <h1>Customer Lookup</h1>

    <form autocomplete="off" @submit="searchUser">
      <b-row class="mb-5 align-items-center">
        <b-col cols="*">
          <label for="id">Customer ID or Email</label>
        </b-col>
        <b-col>
          <b-form-input v-model="id" name="id" autocomplete="off" />
        </b-col>
        <b-col cols="*">
          <b-button @click="searchUser">
            Look Up
          </b-button>
        </b-col>
      </b-row>
    </form>

    <div v-if="loading">
      <fingerprint-spinner :animation-duration="1500" :size="300" color="#293a93" class="my-3" />
    </div>
    <div v-else>
      <b-row v-if="user">
        <b-col>
          <b-row>
            <b-col>
              <h2>{{ user.first_name }} {{ user.last_name }}</h2>
              <dl>
                <dt>Email</dt>
                <dd>
                  {{ user.email }}
                  <a v-if="user.email" href="#" v-b-popover.hover="'Click to Copy Email'" @click="copyToClipboard(user.email)">
                    <i class="fas fa-clipboard"></i>
                  </a>
                </dd>
                <dt>Account Created</dt>
                <dd>
                  <NiceDate :value="user.createdAt" :isUtc="true"/>
                </dd>
              </dl>
            </b-col>
            <b-col>
              <b-button
                variant="outline-primary"
                size="sm"
                target="_blank"
                class="mb-3"
                :href="`https://admin.shopify.com/store/2ca7d3-42/customers/${this.user.id.split('/').at(-1)}`"
              >
                Open Customer on Website
              </b-button>
              <b-alert :show="inviteAlert.show" :variant="inviteAlert.variant" class="my-2">
                {{ inviteAlert.text }}
              </b-alert>
              <b-button variant="warning" size="sm" class="mb-3" @click="sendInviteEmail">
                Send Customer Invite Email
              </b-button>
            </b-col>
          </b-row>
          <b-row align-h="center" align-v="center" class="justify-content-right mt-4">
            <b-col cols="4">
              <h3 class="mr-4">Tax Exemption</h3>
            </b-col>
            <b-col cols="8">
              <!--Right now only gives information over tax exemptions that will expire,
                  probably want to give information about customer's tax exemptions no matter what.
                  So in the future we should pull from shopify to get the tax exemptions instead of just
                  relying on just the status metafield and any exemptions with expirations.-->
              <b-alert show variant="warning" v-if="taxExemptStatus === 'Pending'" class="text-center">
                <div><strong>Customer has PENDING Tax Exemption(s)</strong></div>
                <div v-if="!(taxExpiryDates == undefined || taxExpiryDates.length == 0)"><strong>Also has the following exemptions that will expire:</strong></div>
                <div v-if="!(taxExpiryDates == undefined || taxExpiryDates.length == 0)" v-for="expiry in taxExpiryDates" :key="expiry.state">
                  <strong>{{ expiry.state }}</strong>
                  Expires: <NiceDate :value="expiry.date" :show-time="false" />
                </div>
              </b-alert>
              <b-alert show variant="success" v-else-if="taxExemptStatus === 'Approved'" class="text-center">
                <div><strong>Customer is tax exempt</strong></div>
                <div v-if="!(taxExpiryDates == undefined || taxExpiryDates.length == 0)" v-for="expiry in taxExpiryDates" :key="expiry.state">
                  <strong>{{ expiry.state }}</strong>
                  Expires: <NiceDate :value="expiry.date" :show-time="false" />
                </div>
              </b-alert>
              <b-alert show variant="warning" v-else-if="taxExemptStatus === 'Expired' && !(taxExpiryDates == undefined || taxExpiryDates.length == 0)" class="text-center">
                <div><strong>Customer had an exemption Expire, but still has the following exemptions that will expire: </strong></div>
                <div v-for="expiry in taxExpiryDates" :key="expiry.state">
                  <strong>{{ expiry.state }}</strong>
                  Expires: <NiceDate :value="expiry.date" :show-time="false" />
                </div>
              </b-alert>
              <b-alert show variant="warning" v-else-if="taxExemptStatus === 'Denied' && !(taxExpiryDates == undefined || taxExpiryDates.length == 0)" class="text-center">
                <div><strong>Customer had an exemption Denied, but still has the following exemptions that will expire: </strong></div>
                <div v-for="expiry in taxExpiryDates" :key="expiry.state">
                  <strong>{{ expiry.state }}</strong>
                  Expires: <NiceDate :value="expiry.date" :show-time="false" />
                </div>
              </b-alert>
              <b-alert show v-else variant="danger">
                <div><strong>Customer has no tax exemptions that will expire (Status: {{ taxExemptStatus }})</strong></div>
              </b-alert>
            </b-col>
          </b-row>
          <b-row align-h="center">
            <b-col cols="8">
              <b-alert class="mt-4 text-center" :show="taxAlert.show" :variant="taxAlert.variant">
                {{ taxAlert.text }}
              </b-alert>
            </b-col>
          </b-row>
          <b-row align-h="center">
            <b-col cols="8" v-if="this.user.tags.includes('TAX-TODO') && formsToDo.length > 0">
              <div v-for="tef in formsToDo" :key="tef.id" class="my-2">
                <div>
                  <b-button block variant="outline-primary" :href="tef.url" target="_blank">
                    View {{ parseFormState(tef) }} Tax Form from <NiceDate :value="tef.createdAt" :isUtc="true"/>
                  </b-button>
                  <b-row class="mt-4" v-if="parseFormState(tef) == 'STREAMLINED'">
                    <label for="tef-exempted-states">Exempted States (required)</label>
                    <b-form-checkbox-group :disabled="!!tef.processed" name="tef-exempted-states" class="mx-2 my-1" v-model="tef.taxStates" :options="enabledTaxStates" />
                  </b-row>
                  <b-row class="mt-3">
                    <label for="tef-expiry-date">Expiry Date (optional)</label>
                    <b-form-input :disabled="!!tef.processed" name="tef-expiry-date" type="date" v-model="tef.expiryDate" />
                  </b-row>
                  <b-row class="justify-content-center mt-1">
                    <b-button :disabled="!!tef.processed" variant="success" class="mx-1" @click="approveForm(tef)">
                      Approve Form
                    </b-button>
                  </b-row>
                  <b-row class="mt-3">
                    <label for="tef-reject-reason">Rejection Reason (optional)</label>
                    <b-form-input :disabled="!!tef.processed" name="tef-reject-reason" v-model="tef.rejectReason" />
                  </b-row>
                  <b-row class="justify-content-center mt-1">
                    <b-button :disabled="!!tef.processed" variant="danger" class="mx-1" @click="rejectForm(tef)">
                      Reject Form
                    </b-button>
                    <b-button :disabled="!!tef.processed" variant="secondary" class="mx-1" @click="ignoreForm(tef)">
                      Mark Viewed
                    </b-button>
                  </b-row>
                </div>
                <hr style="border-top: 2px solid rgba(0,0,0,0.5)">
              </div>
            </b-col>
            <b-col cols="8" v-else-if="this.user.tags.includes('TAX-TODO')">
              <b-alert show>
                User is marked TAX-TODO but has no tax exempt forms! Please let IT know.
              </b-alert>
            </b-col>
          </b-row>
          <b-row>
            <div v-if="taxExemptForms.length > 0">
              <h4 class="mt-4 mb-0">Tax Forms:</h4>
              <b-row>
                <b-col cols="6" v-for="tef in taxExemptForms" :key="tef.id" class="mb-1 p-2">
                  <b-btn block :href="tef.url" target="_blank">
                    View {{ parseFormState(tef) }} Tax Form from <NiceDate :value="tef.createdAt" :isUtc="false"/>
                  </b-btn>
                </b-col>
              </b-row>
            </div>
          </b-row>
        </b-col>
        <b-col cols="6">
          <h3>Recent Orders</h3>
          <div v-if="loadingOrders">
            <fingerprint-spinner
              :animation-duration="1500"
              :size="300"
              color="#293a93"
              class="my-3"
            />
          </div>
          <div v-else>
            <b-row v-for="order in this.orders" :key="order.num" class="mb-2">
              <b-col>
                <router-link target="_blank" :to="{name: 'View Order', params: {id: order.num}}">
                  {{ order.num }}
                </router-link>
                <a target="_blank" :href="orderWebsiteLink(order)" v-if="order.websiteName">
                  To {{order.websiteName | titleize}}
                  <i v-if="order.websiteName == 'shopify'" class="shopify-icon" style="height:1em"></i>
                  <i v-if="order.websiteName == 'workarea'" class="wa-icon" style="height:1em"></i>
                </a>
              </b-col>
              <b-col cols=3>
                <CondensedStatus :knownOrder="order" :number="order.num" />
              </b-col>
              <b-col cols=5>
                <NiceDate :value="order.placedAt" :show-time="false" :merge="true" />
              </b-col>
            </b-row>
          </div>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import { FingerprintSpinner } from 'epic-spinners';
import NiceDate from '../utility/NiceDate'
import CondensedStatus from '../utility/CondensedStatus'
import ShopifyService from '../../services/shopify'
import BridgeService from '../../services/bridge';
import InfoService from '../../services/info';
import MergeService from '../../services/orderMerge';
import CommonHelper from '../../helpers/services/common'

export default {
  name: 'CustomerLookup',
  components: {
    NiceDate,
    FingerprintSpinner,
    CondensedStatus,
  },
  data() {
    return {
      id: '',
      user: null,
      orders: [],
      taxExemptForms: [],
      loading: false,
      loadingOrders: false,
      inviteAlert: { text: '', show: false, variant: 'success' },
      taxAlert: { text: '', show: false, variant: 'danger' },
      enabledTaxStates: [],
    }
  },
  async mounted() {
    this.enabledTaxStates = await InfoService.getEnabledTaxStates()
    if (this.$route.query.id) {
      this.id = this.$route.query.id
      this.getUser()
    }
  },
  computed: {
    taxExemptStatus() {
      return ShopifyService.getMetafieldValue(this.user, 'tax_exemption_status') || 'N/A'
    },
    taxExpiryDates() {
      let tags = this.user.tags
      tags = tags.filter(x => x.startsWith('Tax Exemption Expires:'))
      if (tags.length > 0) {
        return tags.map(x => ({
          state: x.split('|').at(-1).trim(),
          date: new Date(x.replace('Tax Exemption Expires:', '').slice(0, 11).trim()).toUTCString()
        }))
      }
      return undefined;
    },
    submittedTaxForms() {
      const mf = ShopifyService.getMetafield(this.user, 'tax_exemption_form_log')
      if (!mf || !mf.references || !mf.references.nodes) return [];

      return mf.references.nodes
    },
    formsToDo() {
      const mf = ShopifyService.getMetafield(this.user, 'tax_exemption_forms_to_do')
      if (!mf || !mf.references || !mf.references.nodes) return [];

      const ids = JSON.parse(mf.value)

      for (let i = 0; i < mf.references.nodes.length; i += 1) {
        mf.references.nodes[i].gid = ids[i]
      }
      return mf.references.nodes
    },
  },
  methods: {
    searchUser(evt) {
      if (evt) {
        evt.preventDefault();
      }
      this.$router.push({ path: 'customers', query: { id: this.id } });
      this.getUser()
    },
    getUser() {
      this.loading = true
      this.loadingOrders = true

      ShopifyService.findShopifyCustomer(this.id.toLowerCase()).then((r) => {
        this.user = r.data;
        this.taxExemptForms = this.submittedTaxForms;
        ShopifyService.searchOrder({ email: this.user.email }).then((rs) => {
          Promise.all(
            rs.sort((a,b) => a.createdAt - b.createdAt)
              .slice(0, 60)
              .map(order => BridgeService.getSalesOrder(order.name))
          ).then((orders) => {
            this.orders = orders
              .map(o => o.data.merged)
              .sort((b,a) => {
                let aTime = MergeService.unifyTime(MergeService.mergeField(a.createdAt)),
                    bTime = MergeService.unifyTime(MergeService.mergeField(b.createdAt));
                return aTime - bTime;
              }).slice(0, 15)
          }).catch((err) => {
            console.error(err)
          }).finally(() => {
            this.loadingOrders = false;
          })
        }).catch((err) => {
          console.error(err)
          this.orders = [];
        })
      }).finally(() => {
        this.loading = false;
      })
    },
    parseFormState(tef) {
      const state = tef.alt.slice(8, tef.alt.indexOf('Tax Form')).trim()
      return state === 'MANY' ? 'STREAMLINED' : state;
    },
    sendInviteEmail() {
      this.inviteAlert.show = false
      ShopifyService.sendInviteEmail(this.user.id.split('/').at(-1)).then((response) => {
        this.inviteAlert.text = `Invite sent to ${response.data.customerSendAccountInviteEmail.customer.defaultEmailAddress.emailAddress}!`
        this.inviteAlert.variant = 'success'
        this.inviteAlert.show = true
      }).catch((error) => {
        console.error(error)
        this.inviteAlert.text = 'Something went wrong! Details were logged to your browser console. Please don\'t close this tab and report this error to IT'
        this.inviteAlert.variant = 'danger'
        this.inviteAlert.show = true
      })
    },
    approveForm(tef) {
      this.taxAlert.text = ''
      const state = this.parseFormState(tef);
      if (state === 'STREAMLINED' && (!tef.taxStates || tef.taxStates.length === 0)) {
        this.taxAlert.text = 'Need to select at least 1 state for streamlined tax form';
        this.taxAlert.variant = 'danger'
        this.taxAlert.show = true
        return
      } else if (state !== 'STREAMLINED') {
        // eslint-disable-next-line no-param-reassign
        tef.taxStates = [state]
      }

      ShopifyService.processTaxForm(this.user.id, tef, 'approve').then((response) => {
        if (response.data.customerUpdate.customer) {
          this.taxAlert.text = 'Tax Exempt Form Approved!'
          this.taxAlert.variant = 'success'
          this.taxAlert.show = true
          // eslint-disable-next-line no-param-reassign
          tef.processed = true
        }
      }).finally(() => {
        if (this.taxAlert.text === '') {
          this.taxAlert.text = 'Something went wrong! Please report this error to IT, and tell them what customer this happened on.'
          this.taxAlert.variant = 'danger'
          this.taxAlert.show = true
        }
      })
    },
    rejectForm(tef) {
      this.taxAlert.text = ''
      ShopifyService.processTaxForm(this.user.id, tef, 'reject').then((response) => {
        if (response.data.customerUpdate.customer) {
          this.taxAlert.text = 'Tax Exempt Form Declined!'
          this.taxAlert.variant = 'success'
          this.taxAlert.show = true
          // eslint-disable-next-line no-param-reassign
          tef.processed = true
        }
      }).finally(() => {
        if (this.taxAlert.text === '') {
          this.taxAlert.text = 'Something went wrong! Please report this error to IT, and tell them what customer this happened on.'
          this.taxAlert.variant = 'danger'
          this.taxAlert.show = true
        }
      })
    },
    ignoreForm(tef) {
      this.taxAlert.text = ''
      ShopifyService.processTaxForm(this.user.id, tef, 'ignore').then((response) => {
        if (response.data.customerUpdate.customer) {
          this.taxAlert.text = 'Tax Exempt Form Marked Viewed!'
          this.taxAlert.variant = 'success'
          this.taxAlert.show = true
          // eslint-disable-next-line no-param-reassign
          tef.processed = true
        }
      }).finally(() => {
        if (this.taxAlert.text === '') {
          this.taxAlert.text = 'Something went wrong! Please report this error to IT, and tell them what customer this happened on.'
          this.taxAlert.variant = 'danger'
          this.taxAlert.show = true
        }
      })
    },
    copyToClipboard(text) {
      if (window.clipboardData && window.clipboardData.setData) {
        // IE specific code path to prevent textarea being shown while dialog is visible.
        return window.clipboardData.setData('Text', text);
      } else if (document.queryCommandSupported && document.queryCommandSupported('copy')) {
        const textarea = document.createElement('textarea');
        textarea.textContent = text;
        textarea.style.position = 'fixed'; // Prevent scrolling to bottom of page in MS Edge.
        document.body.appendChild(textarea);
        textarea.select();
        try {
          return document.execCommand('copy'); // Security exception may be thrown by some browsers.
        } catch (ex) {
          console.warn('Copy to clipboard failed.', ex);
        } finally {
          document.body.removeChild(textarea);
        }
      }
      return false;
    },
    orderWebsiteLink(order) {
      return CommonHelper.orderWebsiteLink(order);
    }
  },
}
</script>
<style>
.container {
  max-width: 1500px !important;
}
</style>



// WEBPACK FOOTER //
// src/components/customers/CustomerLookup.vue